import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _classPrivateFieldGet from '@babel/runtime/helpers/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/classPrivateFieldSet';
import { testReducedMotion, debounce } from '@nrk/dh-basis-common';
import scrollParent from 'scrollparent';

function parseOffsetString(offsetString, target) {
  return offsetString.split(',').map(offset => offset.trim()).map(offset => {
    if (offset === '' || offset === 'auto') {
      return 'auto';
    } else if (offset.match(/^\d*%$/)) {
      return offset;
    } else if (offset.match(/^(\d*)px$/)) {
      return offset;
    } else {
      var _offset$match;

      var edge;

      if (offset.match(/start/)) {
        edge = 'start';
      } else if (offset.match(/end/)) {
        edge = 'end';
      }

      var threshold;
      var componentValue = (_offset$match = offset.match(/\d+\.?\d*/)) === null || _offset$match === void 0 ? void 0 : _offset$match[0];

      if (componentValue) {
        threshold = parseFloat(componentValue);
      }

      return {
        target,
        edge,
        threshold
      };
    }
  });
}

var horizontalProperties = {
  dimension: 'width',
  position: 'left',
  scrollDimension: 'scrollWidth',
  clientDimension: 'clientWidth',
  scrollPosition: 'scrollLeft',
  scrollPaddingStart: 'scrollPaddingLeft',
  scrollPaddingEnd: 'scrollPaddingRight'
};
var verticalProperties = {
  dimension: 'height',
  position: 'top',
  scrollDimension: 'scrollHeight',
  clientDimension: 'clientHeight',
  scrollPosition: 'scrollTop',
  scrollPaddingStart: 'scrollPaddingTop',
  scrollPaddingEnd: 'scrollPaddingBottom'
};
function getDirectionPropNames(direction, source) {
  if (direction === 'horizontal') {
    return horizontalProperties;
  } else if (direction === 'vertical') {
    return verticalProperties;
  }

  var writingMode = getComputedStyle(source)['writing-mode'];
  var blockAxis = writingMode === 'horizontal-tb' ? 'vertical' : 'horizontal';

  if (direction === 'block') {
    return blockAxis === 'vertical' ? verticalProperties : horizontalProperties;
  } else if (direction === 'inline') {
    return blockAxis === 'vertical' ? horizontalProperties : verticalProperties;
  }

  return verticalProperties;
}

var scrollOverflowRegex = /(auto|scroll)/;
/*
 * Parse effective scroll offsets as defined in
 * https://drafts.csswg.org/scroll-animations-1/#effective-scroll-offsets-algorithm
 * */

function calculateEffectiveScrollOffsets(scrollOffsets, source, direction, scrollPadding) {
  var effectiveScrollOffsets = [];
  var firstOffset = true;

  if (scrollOffsets.length === 0) {
    effectiveScrollOffsets.push(calculateEffectiveOffset(source, 'auto', direction, scrollPadding, firstOffset));
    firstOffset = false;
    effectiveScrollOffsets.push(calculateEffectiveOffset(source, 'auto', direction, scrollPadding, firstOffset));
  }

  if (scrollOffsets.length === 1) {
    effectiveScrollOffsets.push(calculateEffectiveOffset(source, 'auto', direction, scrollPadding, firstOffset));
    firstOffset = false;
  }

  for (var scrollOffset of scrollOffsets) {
    var effectiveOffset = calculateEffectiveOffset(source, scrollOffset, direction, scrollPadding, firstOffset);

    if (effectiveOffset === null) {
      return [];
    }

    effectiveScrollOffsets.push(effectiveOffset);
    firstOffset = false;
  }

  return effectiveScrollOffsets;
}

function calculateEffectiveOffset(source, offset, direction, scrollPadding, first) {
  if (typeof offset === 'string') {
    return calculateContainerBasedOffset(source, offset, direction, first);
  } else if (offset !== null && offset !== void 0 && offset.target) {
    return calculateElementBasedOffset(source, offset, direction, scrollPadding);
  }

  return null;
}
/*
 * Calculate container based offset as defiened in
 * https://drafts.csswg.org/scroll-animations-1/#container-based-offset-section
 */


function calculateContainerBasedOffset(source, offset, direction, first) {
  if (!source || !hasLayoutBox(source)) {
    return null;
  }

  if (!isScrollContainer(source)) {
    return null;
  } else if (offset === 'auto') {
    if (first) {
      return getSourceStart();
    } else {
      return getSourceEnd(source, direction);
    }
  } else if (typeof offset === 'string' && offset.endsWith('px')) {
    return parseFloat(offset.substring(0, offset.length - 2));
  } else if (typeof offset === 'string' && offset.endsWith('%')) {
    var percentValue = parseFloat(offset.substring(0, offset.length - 1));
    var scrollRange = getSourceEnd(source, direction);
    return percentValue * scrollRange / 100.0;
  }
}
/* Calculate element based offsets as defined in
 * https://drafts.csswg.org/scroll-animations-1/#element-based-offset-section
 *
 * Deviating from standard by supporting scroll-padding
 * */

function calculateElementBasedOffset(source, offset, direction, scrollPadding) {
  var {
    target,
    edge,
    threshold
  } = offset;
  var props = getDirectionPropNames(direction, source);

  if (!source || !hasLayoutBox(source)) {
    console.log('Missing or hidden source');
    return null;
  }

  if (!isScrollContainer(source) || !target || !hasLayoutBox(target)) {
    return null;
  }

  var ancestor = getNearestScrollContainerAncestor(target);

  if (!isSameScrollcontainer(ancestor, source)) {
    console.warn('Target does not have source as nearest scrollport', source, ancestor);
    return null;
  }

  var targetBox = getAxisAlignedTargetBox(source, target);
  var scrollOffset;

  if (edge === 'start') {
    var _scrollPadding$props$;

    var startEdge = targetBox[props.position] + targetBox[props.dimension] - ((_scrollPadding$props$ = scrollPadding === null || scrollPadding === void 0 ? void 0 : scrollPadding[props.scrollPaddingStart]) !== null && _scrollPadding$props$ !== void 0 ? _scrollPadding$props$ : 0);
    scrollOffset = startEdge - threshold * targetBox[props.dimension];
  } else if (edge === 'end') {
    var _scrollPadding$props$2;

    var endEdge = targetBox[props.position] - getClientDimension(source, props) + ((_scrollPadding$props$2 = scrollPadding === null || scrollPadding === void 0 ? void 0 : scrollPadding[props.scrollPaddingEnd]) !== null && _scrollPadding$props$2 !== void 0 ? _scrollPadding$props$2 : 0);
    scrollOffset = endEdge + threshold * targetBox[props.dimension];
  }

  return clamp(scrollOffset, getSourceStart(), getSourceEnd(source, direction));
}

function isSameScrollcontainer(a, b) {
  return a === b || // eslint-disable-next-line compat/compat
  a === document.documentElement && b === document.scrollingElement || // eslint-disable-next-line compat/compat
  a === document.scrollingElement && b === document.documentElement;
}

function getAxisAlignedTargetBox(source, target) {
  var targetRect = target.getBoundingClientRect();

  if (source === document.scrollingElement) {
    return {
      top: targetRect.top + source.scrollTop,
      left: targetRect.left + source.scrollLeft,
      width: targetRect.width,
      height: targetRect.height
    };
  } else {
    var containerRect = source.getBoundingClientRect();
    return {
      top: targetRect.top - containerRect.top + source.scrollTop,
      left: targetRect.left - containerRect.left + source.scrollLeft,
      width: targetRect.width,
      height: targetRect.height
    };
  }
}

function clamp(value, min, max) {
  return Math.max(Math.min(value, max), min);
}

function getSourceStart() {
  return 0;
}

function getClientDimension(source, propNames) {
  if (source === document.scrollingElement) {
    // In Safari <=12 body is the scrolling element, while client height can be found on the root.
    return Math.min(document.scrollingElement[propNames.clientDimension], document.documentElement[propNames.clientDimension]);
  } else {
    return source[propNames.clientDimension];
  }
}

function getSourceEnd(source, direction) {
  var propNames = getDirectionPropNames(direction);
  var clientDimension = getClientDimension(source, propNames);
  return source[propNames.scrollDimension] - clientDimension;
}

function hasLayoutBox(element) {
  return Boolean(element.offsetWidth && element.offsetHeight);
}
/* Caches the result and does not update if styles change */


function getNearestScrollContainerAncestor(target) {
  return scrollParent(target);
}

function isScrollContainer(source) {
  var computed = getComputedStyle(source);
  var overflowString = computed.overflow + computed['overflow-x'] + computed['overflow-y']; // eslint-disable-next-line compat/compat

  var rootScrollElement = document.scrollingElement || document.documentElement;
  return source === rootScrollElement || scrollOverflowRegex.test(overflowString);
}

function calculateScrollProgress(source, scrollOffsets, direction) {
  var propNames = getDirectionPropNames(direction, source);
  var offset = source[propNames.scrollPosition];
  var firstOffset = scrollOffsets[0];
  /* Ranges outside the first and last offset is not covered in the spec for progress. Clamping */

  var lastOffset = scrollOffsets[scrollOffsets.length - 1];

  if (offset < firstOffset) {
    return 0;
  } else if (offset >= lastOffset) {
    return 1;
  }

  var offsetIndex;

  for (var i = 0; i < scrollOffsets.length; i++) {
    var scrollOffset = scrollOffsets[i];

    if (scrollOffset <= offset) {
      offsetIndex = i;
    }
  }

  var startOffset = scrollOffsets[offsetIndex];
  var endOffset = scrollOffsets[offsetIndex + 1];
  var offsetWeight = 1 / (scrollOffsets.length - 1);
  var intervalProgress = (offset - startOffset) / (endOffset - startOffset);
  return (offsetIndex + intervalProgress) * offsetWeight;
}

function getScrollportHeight(source) {
  if (source === document.scrollingElement || source === document.documentElement) {
    // Use innerHeight when source is root element
    return window.innerHeight;
  } else {
    return source.clientHeight;
  }
}
function getScrollportWidth(source) {
  if (source === document.scrollingElement || source === document.documentElement) {
    // Use innerHeight when source is root element
    return window.innerWidth;
  } else {
    return source.clientWidth;
  }
}

/*
 * Calculate effective scroll paddings as pixel values.
 * Supports pixel and percentage values resolved relative to source.
 *
 * Viewport units are not supporteds
 *
 * https://drafts.csswg.org/css-scroll-snap-1/#propdef-scroll-padding
 **/
function calculateEffectiveScrollPaddings(scrollPaddings, source) {
  var result = {};

  for (var [key, value] of Object.entries(scrollPaddings)) {
    if (value.endsWith('px')) {
      result[key] = parseFloat(value.substring(0, value.length - 2));
    }

    if (value.endsWith('%')) {
      var p = parseFloat(value.substring(0, value.length - 1));

      if (['scrollPaddingTop', 'scrollPaddingBottom'].includes(key)) {
        result[key] = p * getScrollportHeight(source) / 100.0;
      } else if (['scrollPaddingLeft', 'scrollPaddingRight'].includes(key)) {
        result[key] = p * getScrollportWidth(source) / 100.0;
      }
    }
  }

  return result;
}

/*
 * Parse scroll padding shorthand string the same way padding strings:
 *
 * https://drafts.csswg.org/css-box-4/#propdef-padding
 * */
function parseScrollPadding() {
  var paddingString = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var padding = paddingString.split(' ').filter(Boolean);

  if (padding.length === 0) {
    return {
      scrollPaddingTop: '0px',
      scrollPaddingRight: '0px',
      scrollPaddingBottom: '0px',
      scrollPaddingLeft: '0px'
    };
  } else if (padding.length === 1) {
    return {
      scrollPaddingTop: padding[0],
      scrollPaddingRight: padding[0],
      scrollPaddingBottom: padding[0],
      scrollPaddingLeft: padding[0]
    };
  } else if (padding.length === 2) {
    var [vertical, horizontal] = padding;
    return {
      scrollPaddingTop: vertical,
      scrollPaddingRight: horizontal,
      scrollPaddingBottom: vertical,
      scrollPaddingLeft: horizontal
    };
  } else if (padding.length === 3) {
    var [top, _horizontal, bottom] = padding;
    return {
      scrollPaddingTop: top,
      scrollPaddingRight: _horizontal,
      scrollPaddingBottom: bottom,
      scrollPaddingLeft: _horizontal
    };
  } else if (padding.length === 4) {
    var [_top, right, _bottom, left] = padding;
    return {
      scrollPaddingTop: _top,
      scrollPaddingRight: right,
      scrollPaddingBottom: _bottom,
      scrollPaddingLeft: left
    };
  }
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _classPrivateMethodInitSpec(obj, privateSet) { _checkPrivateRedeclaration(obj, privateSet); privateSet.add(obj); }

function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }

function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
/*
 * Mixin for custom elements
 *
 * Keeps track of multiple scroll timelines and calls updatedCallback whenever
 * progress in a scroll timeline has changed.
 *
 * Inspired by https://drafts.csswg.org/scroll-animations-1/
 */

function ScrollanimasjonMixin(Base) {
  var _effectiveScrollPaddings, _effectiveScrollTimelines, _isWatching, _reducedMotion, _observer, _debouncedUpdateOffsets, _targetPositions, _createIntersectionObserver, _disconnectIntersectionObserver, _handleVisibilityChange, _getTargetPositions, _needsEffectiveOffsetUpdate, _updateEffectiveOffsets, _startWatching, _stopWatching;

  return _effectiveScrollPaddings = /*#__PURE__*/new WeakMap(), _effectiveScrollTimelines = /*#__PURE__*/new WeakMap(), _isWatching = /*#__PURE__*/new WeakMap(), _reducedMotion = /*#__PURE__*/new WeakMap(), _observer = /*#__PURE__*/new WeakMap(), _debouncedUpdateOffsets = /*#__PURE__*/new WeakMap(), _targetPositions = /*#__PURE__*/new WeakMap(), _createIntersectionObserver = /*#__PURE__*/new WeakSet(), _disconnectIntersectionObserver = /*#__PURE__*/new WeakSet(), _handleVisibilityChange = /*#__PURE__*/new WeakMap(), _getTargetPositions = /*#__PURE__*/new WeakSet(), _needsEffectiveOffsetUpdate = /*#__PURE__*/new WeakSet(), _updateEffectiveOffsets = /*#__PURE__*/new WeakMap(), _startWatching = /*#__PURE__*/new WeakSet(), _stopWatching = /*#__PURE__*/new WeakSet(), class extends Base {
    constructor() {
      super(...arguments);

      _classPrivateMethodInitSpec(this, _stopWatching);

      _classPrivateMethodInitSpec(this, _startWatching);

      _classPrivateMethodInitSpec(this, _needsEffectiveOffsetUpdate);

      _classPrivateMethodInitSpec(this, _getTargetPositions);

      _classPrivateMethodInitSpec(this, _disconnectIntersectionObserver);

      _classPrivateMethodInitSpec(this, _createIntersectionObserver);

      _classPrivateFieldInitSpec(this, _effectiveScrollPaddings, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _effectiveScrollTimelines, {
        writable: true,
        value: []
      });

      _classPrivateFieldInitSpec(this, _isWatching, {
        writable: true,
        value: false
      });

      _classPrivateFieldInitSpec(this, _reducedMotion, {
        writable: true,
        value: testReducedMotion()
      });

      _classPrivateFieldInitSpec(this, _observer, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _debouncedUpdateOffsets, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _targetPositions, {
        writable: true,
        value: []
      });

      _classPrivateFieldInitSpec(this, _handleVisibilityChange, {
        writable: true,
        value: _ref => {
          var [containerIntersection] = _ref;

          if (containerIntersection.isIntersecting) {
            _classPrivateMethodGet(this, _startWatching, _startWatching2).call(this);
          } else {
            _classPrivateMethodGet(this, _stopWatching, _stopWatching2).call(this);
          }
        }
      });

      _classPrivateFieldInitSpec(this, _updateEffectiveOffsets, {
        writable: true,
        value: () => {
          // Record offset target positions so that we can detect changes in layout.
          _classPrivateFieldSet(this, _targetPositions, _classPrivateMethodGet(this, _getTargetPositions, _getTargetPositions2).call(this));

          _classPrivateFieldSet(this, _effectiveScrollPaddings, calculateEffectiveScrollPaddings(this.scrollPadding, this.source));

          _classPrivateFieldSet(this, _effectiveScrollTimelines, this.timelines.map(timeline => {
            return _objectSpread(_objectSpread({}, timeline), {}, {
              effectiveScrollOffsets: calculateEffectiveScrollOffsets(timeline.scrollOffsets, this.source, this.direction, _classPrivateFieldGet(this, _effectiveScrollPaddings))
            });
          }));
        }
      });
    }

    connectedCallback() {
      var _super$connectedCallb;

      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 ? void 0 : _super$connectedCallb.call(this); // eslint-disable-next-line compat/compat

      var isSupported = !!document.scrollingElement;

      if (!isSupported) {
        return;
      }

      _classPrivateFieldSet(this, _reducedMotion, testReducedMotion());

      if (_classPrivateFieldGet(this, _reducedMotion)) {
        // Default to timeline end if user request reduced motion
        for (var timeline of this.timelines) {
          this.timelineUpdatedCallback(timeline.id, 1);
        }

        return;
      }

      _classPrivateFieldGet(this, _updateEffectiveOffsets).call(this);

      _classPrivateFieldSet(this, _debouncedUpdateOffsets, debounce(_classPrivateFieldGet(this, _updateEffectiveOffsets), 200));

      window.addEventListener('resize', _classPrivateFieldGet(this, _debouncedUpdateOffsets));
      this.addEventListener('dh-basis-size-changed', _classPrivateFieldGet(this, _debouncedUpdateOffsets));

      _classPrivateMethodGet(this, _createIntersectionObserver, _createIntersectionObserver2).call(this);
    }

    disconnectedCallback() {
      var _super$disconnectedCa;

      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 ? void 0 : _super$disconnectedCa.call(this);
      window.removeEventListener('resize', _classPrivateFieldGet(this, _debouncedUpdateOffsets));
      this.removeEventListener('dh-basis-size-changed', _classPrivateFieldGet(this, _debouncedUpdateOffsets));

      _classPrivateMethodGet(this, _disconnectIntersectionObserver, _disconnectIntersectionObserver2).call(this);

      _classPrivateMethodGet(this, _stopWatching, _stopWatching2).call(this);
    }

    /* Override this method to perform updates */
    timelineUpdatedCallback(timelineId, progress) {
      console.log(timelineId, progress.toFixed(3));
    }

    get timelines() {
      return [{
        id: 'scrollprosent',
        scrollOffsets: [{
          target: this,
          edge: 'end',
          threshold: 0
        }, {
          target: this,
          edge: 'start',
          threshold: 0
        }]
      }];
    }

    get source() {
      // eslint-disable-next-line compat/compat
      return document.scrollingElement;
    }

    get direction() {
      /* block, inline, horizontal, vertical */
      return 'vertical';
    }
    /*
     * Set scroll padding as defined in https://drafts.csswg.org/css-scroll-snap-1/#propdef-scroll-padding
     */


    get scrollPadding() {
      var paddingString = this.getAttribute('scroll-padding') || '0px 0px 0px 0px';
      return parseScrollPadding(paddingString);
    }

  };

  function _createIntersectionObserver2() {
    // eslint-disable-next-line compat/compat
    _classPrivateFieldSet(this, _observer, new IntersectionObserver(_classPrivateFieldGet(this, _handleVisibilityChange), {
      /*
      Use document as root, so that intersections are tracked against iframe scrolling viewport if embedded (polopoly-preview).
      (No root would use the viewport, even when embedded in iframe)
      https://w3c.github.io/IntersectionObserver/#dom-intersectionobserver-root
      */
      root: document,
      threshold: [0]
    }));

    _classPrivateFieldGet(this, _observer).observe(this);
  }

  function _disconnectIntersectionObserver2() {
    if (_classPrivateFieldGet(this, _observer)) {
      _classPrivateFieldGet(this, _observer).disconnect();
    }
  }

  function _getTargetPositions2() {
    return this.timelines.flatMap(timeline => timeline.scrollOffsets).map(scrollOffset => scrollOffset.target).map(target => {
      var propNames = getDirectionPropNames(this.direction, this.source);
      return target.getBoundingClientRect()[propNames.position] + this.source[propNames.scrollPosition];
    });
  }

  function _needsEffectiveOffsetUpdate2() {
    var currentTargetPositions = _classPrivateMethodGet(this, _getTargetPositions, _getTargetPositions2).call(this);

    for (var [index, position] of currentTargetPositions.entries()) {
      if (_classPrivateFieldGet(this, _targetPositions)[index] !== position) {
        return true;
      }
    }
  }

  function _startWatching2() {
    if (_classPrivateFieldGet(this, _isWatching)) {
      return;
    }

    if (_classPrivateMethodGet(this, _needsEffectiveOffsetUpdate, _needsEffectiveOffsetUpdate2).call(this)) {
      _classPrivateFieldGet(this, _updateEffectiveOffsets).call(this);
    }

    var loop = () => {
      if (_classPrivateFieldGet(this, _isWatching)) {
        requestAnimationFrame(loop);
      } else {
        return;
      }

      for (var timeline of _classPrivateFieldGet(this, _effectiveScrollTimelines)) {
        var progress = calculateScrollProgress(this.source, timeline.effectiveScrollOffsets, this.direction);

        if (progress !== timeline.progress) {
          this.timelineUpdatedCallback(timeline.id, progress);
        }

        timeline.progress = progress;
      }
    };

    _classPrivateFieldSet(this, _isWatching, true);

    requestAnimationFrame(loop);
  }

  function _stopWatching2() {
    _classPrivateFieldSet(this, _isWatching, false);
  }
}

class Scrollprosent extends ScrollanimasjonMixin(HTMLElement) {
  timelineUpdatedCallback(timelineId, progress) {
    var _this$targetElement;

    (_this$targetElement = this.targetElement) === null || _this$targetElement === void 0 ? void 0 : _this$targetElement.setAttribute('data-percent', progress);
  }

  get targetElement() {
    return this.querySelector('[data-percent]');
  }

  get timelines() {
    return [{
      id: 'scrollprosent',
      scrollOffsets: this.scrollOffsets
    }];
  }
  /*
  Get scroll offsets according to @scroll-timeline definition.
  */


  get scrollOffsets() {
    var _this$getAttribute;

    var offsetString = (_this$getAttribute = this.getAttribute('scroll-offsets')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : '';
    return parseOffsetString(offsetString, this);
  }

}

export { ScrollanimasjonMixin, Scrollprosent, parseOffsetString };
//# sourceMappingURL=dh-basis-scrollanimasjon.esm.js.map
